/**
 * MenuSelect dropdown utilities.
 * Positions the open panel so it always stays inside the viewport and
 * provides outside-click and Escape dismissal.
 */

'use strict';

import type { DotNetObjectReference } from '../types.js';

interface MenuHandle {
    root: HTMLElement;
    panel: HTMLElement;
    clickHandler: (event: MouseEvent) => void;
    keyHandler: (event: KeyboardEvent) => void;
    scrollHandler: () => void;
    dismissOnOutsideClick: boolean;
}

/**
 * Outside presses are caught in the capture phase on pointerdown, so both mouse buttons
 * dismiss (a right-click fires no click event) and the press is seen before the target handles it.
 */
const OUTSIDE_EVENT = 'pointerdown';
const OUTSIDE_CAPTURE = true;

/**
 * Map to store handlers for each menu root element.
 */
const handles = new Map<HTMLElement, MenuHandle>();

/**
 * Positions the panel in fixed viewport coordinates so the page layout can
 * never push it outside the screen: horizontally clamped with a margin,
 * below the trigger when there is room, flipped above when there is not.
 *
 * @param trigger - The trigger element used as the anchor
 * @param panel - The panel element to position
 */
function positionPanel(trigger: HTMLElement, panel: HTMLElement): void {
    const margin = 8;
    const gap = 4;

    const triggerRect = trigger.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Prefer aligning the panel's inline-end edge with the trigger's
    // inline-end edge (matches the RTL placement), then clamp both sides.
    let left = triggerRect.right - panelRect.width;
    left = Math.min(Math.max(margin, left), viewportWidth - margin - panelRect.width);

    let top = triggerRect.bottom + gap;
    if (top + panelRect.height > viewportHeight - margin && triggerRect.top - panelRect.height - gap >= margin) {
        top = triggerRect.top - panelRect.height - gap;
    }
    top = Math.min(Math.max(margin, top), Math.max(margin, viewportHeight - margin - panelRect.height));

    panel.style.position = 'fixed';
    panel.style.top = `${Math.round(top)}px`;
    panel.style.left = `${Math.round(left)}px`;

    // Reveal the panel now that it is at its final position.
    panel.classList.remove('invisible');
}

/**
 * Positions the panel at a viewport point (the cursor for a context menu), keeping
 * it fully inside the viewport.
 *
 * @param panel - The panel element to position
 * @param x - Viewport x of the anchor point
 * @param y - Viewport y of the anchor point
 */
function positionPanelAt(panel: HTMLElement, x: number, y: number): void {
    const margin = 8;

    const panelRect = panel.getBoundingClientRect();
    const left = Math.min(Math.max(margin, x), Math.max(margin, window.innerWidth - margin - panelRect.width));
    const top = Math.min(Math.max(margin, y), Math.max(margin, window.innerHeight - margin - panelRect.height));

    panel.style.position = 'fixed';
    panel.style.top = `${Math.round(top)}px`;
    panel.style.left = `${Math.round(left)}px`;

    // Reveal the panel now that it is at its final position.
    panel.classList.remove('invisible');
}

/**
 * Open a context menu at a viewport point. Dismissed by an outside click, Escape, or any
 * scroll; the panel itself is the root, so clicks inside it never dismiss.
 *
 * @param dotNetRef - Reference to the Blazor component
 * @param panel - The open panel element
 * @param x - Viewport x of the cursor
 * @param y - Viewport y of the cursor
 */
export function initializeContextMenu(
    dotNetRef: DotNetObjectReference,
    panel: HTMLElement | null,
    x: number,
    y: number
): void {
    if (!panel || !dotNetRef) {
        return;
    }

    disposeMenu(panel);

    const clickHandler = (event: MouseEvent): void => {
        if (!panel.contains(event.target as Node)) {
            dotNetRef.invokeMethodAsync('CloseMenu').catch(() => {
                // Silently handle disposal/navigation cases
            });
        }
    };

    const keyHandler = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
            dotNetRef.invokeMethodAsync('CloseMenu').catch(() => {
                // Silently handle disposal/navigation cases
            });
        }
    };

    const scrollHandler = (): void => {
        dotNetRef.invokeMethodAsync('CloseMenu').catch(() => {
            // Silently handle disposal/navigation cases
        });
    };

    handles.set(panel, { root: panel, panel, clickHandler, keyHandler, scrollHandler, dismissOnOutsideClick: true });

    document.addEventListener(OUTSIDE_EVENT, clickHandler, OUTSIDE_CAPTURE);
    document.addEventListener('keydown', keyHandler);
    document.addEventListener('scroll', scrollHandler, { capture: true, passive: true });

    positionPanelAt(panel, x, y);
}

/**
 * Initialize outside-click and Escape dismissal plus viewport-safe
 * positioning for an open menu.
 *
 * @param dotNetRef - Reference to the Blazor component
 * @param root - The menu root element (trigger + panel)
 * @param panel - The open panel element
 */
export function initializeMenu(
    dotNetRef: DotNetObjectReference,
    root: HTMLElement | null,
    panel: HTMLElement | null,
    dismissOnOutsideClick = true
): void {
    if (!root || !panel || !dotNetRef) {
        return;
    }

    disposeMenu(root);

    const clickHandler = (event: MouseEvent): void => {
        const target = event.target as Node;
        if (!root.contains(target)) {
            dotNetRef.invokeMethodAsync('CloseMenu').catch(() => {
                // Silently handle disposal/navigation cases
            });
        }
    };

    const keyHandler = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') {
            dotNetRef.invokeMethodAsync('CloseMenu').catch(() => {
                // Silently handle disposal/navigation cases
            });
        }
    };

    // The panel is fixed-positioned, so scrolling detaches it from its trigger.
    // Close instead of repositioning (native select behavior). Capture catches
    // scrolls of any inner scrollable container, not just the document.
    const scrollHandler = (): void => {
        dotNetRef.invokeMethodAsync('CloseMenu').catch(() => {
            // Silently handle disposal/navigation cases
        });
    };

    handles.set(root, { root, panel, clickHandler, keyHandler, scrollHandler, dismissOnOutsideClick });

    if (dismissOnOutsideClick) {
        document.addEventListener(OUTSIDE_EVENT, clickHandler, OUTSIDE_CAPTURE);
    }
    document.addEventListener('keydown', keyHandler);
    document.addEventListener('scroll', scrollHandler, { capture: true, passive: true });

    positionPanel(root, panel);
}

/**
 * Remove dismissal handlers for a menu root element.
 *
 * @param root - The menu root element
 */
export function disposeMenu(root: HTMLElement | null): void {
    if (!root) {
        return;
    }

    const handle = handles.get(root);
    if (handle) {
        if (handle.dismissOnOutsideClick) {
            document.removeEventListener(OUTSIDE_EVENT, handle.clickHandler, OUTSIDE_CAPTURE);
        }
        document.removeEventListener('keydown', handle.keyHandler);
        document.removeEventListener('scroll', handle.scrollHandler, true);
        handles.delete(root);
    }
}

/**
 * Dispose all menu handlers.
 * Called during cleanup or navigation.
 */
export function disposeAll(): void {
    handles.forEach((handle) => {
        document.removeEventListener(OUTSIDE_EVENT, handle.clickHandler, OUTSIDE_CAPTURE);
        document.removeEventListener('keydown', handle.keyHandler);
        document.removeEventListener('scroll', handle.scrollHandler, true);
    });
    handles.clear();
}
